/**
 *
 * This component handles both card checkout and digital payment
 *
 */

import React, { useCallback, useEffect, useState } from "react";
import {
    CardElement,
    PaymentRequestButtonElement,
    useStripe,
    useElements,
} from "@stripe/react-stripe-js";
import type {
    PaymentMethod,
    PaymentRequest,
    CanMakePaymentResult,
} from "@stripe/stripe-js";
import type { StripeCardElementOptions } from "@stripe/stripe-js";
import "./LoyaltyOfferCheckoutCardForm.scss";
import lockIcon from "../../../static/img/icon-lock-black@2x.png";

type LoyaltyOfferCheckoutCardFormProps = {
    email: string;
    userId: string;
    onPaymentMethodCreated: (pm: PaymentMethod) => void;
    submissionErrorText?: string;
};

const LoyaltyOfferCheckoutCardForm: React.FC<LoyaltyOfferCheckoutCardFormProps> = ({
    email,
    userId,
    onPaymentMethodCreated,
    submissionErrorText = "",
}) => {
    const stripe = useStripe();
    const elements = useElements();
    const isMobile =
        typeof window !== "undefined" &&
        window.matchMedia("(max-width: 850px)").matches;

    const cardOptions: StripeCardElementOptions = React.useMemo(
        () => ({
            style: {
                base: {
                    fontSize: "16px",
                    color: isMobile ? "#0f172a" : "#e6edf3",
                    "::placeholder": {
                        color: isMobile ? "#64748b" : "#8a97a3",
                    },
                    fontFamily:
                        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif',
                },
                invalid: { color: isMobile ? "#b91c1c" : "#ffa9a9" },
            },
            hidePostalCode: false,
            iconStyle: "solid",
        }),
        [isMobile],
    );

    // Payment Request Button (Apple/Google Pay)
    const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(
        null,
    );
    const [canMake, setCanMake] = useState<CanMakePaymentResult | null>(null);

    useEffect(() => {
        if (!stripe) return;

        const pr = stripe.paymentRequest({
        country: "US",
        currency: "usd",

        // Apple Pay total = amount charged now
        total: { label: "IHR Premium 50% Off", amount: 2999 },

        displayItems: [
            { label: "IHR Premium 50% Off", amount: 2999 },
            { label: "Total", amount: 2999},
        ],

        requestPayerName: true,
        requestPayerEmail: true,
        });

        pr.on("paymentmethod", (ev) => {
            onPaymentMethodCreated(ev.paymentMethod);
            ev.complete("success");
        });

        pr.canMakePayment().then((r) => setCanMake(r || null));
        setPaymentRequest(pr);
    }, [stripe, onPaymentMethodCreated]);

    // Card flow
    const [nameOnCard, setNameOnCard] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(
        submissionErrorText,
    );

    const handleSubmit = useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault();
            setErrorMsg(null);
            if (!stripe || !elements) return;

            const card = elements.getElement(CardElement);
            if (!card) {
                setErrorMsg("Card field not ready. Please try again.");
                return;
            }

            setSubmitting(true);
            const { error, paymentMethod } = await stripe.createPaymentMethod({
                type: "card",
                card,
                billing_details: {
                    name: nameOnCard || undefined,
                    email: email || undefined,
                },
            });

            if (error || !paymentMethod) {
                setErrorMsg(
                    error?.message ?? "Unable to create payment method.",
                );
                setSubmitting(false);
                return;
            }

            try {
                onPaymentMethodCreated(paymentMethod);
            } finally {
                setSubmitting(false);
            }
        },
        [stripe, elements, nameOnCard, email, onPaymentMethodCreated],
    );

    const disabled = submitting || !stripe || !elements;

    return (
        <form className="LoyaltyOfferCheckoutCardForm" onSubmit={handleSubmit} noValidate>
            {paymentRequest && canMake && (
                <>
                    <div className="LoyaltyOfferCheckoutCardForm__prb">
                        <PaymentRequestButtonElement
                            options={{
                                paymentRequest,
                                style: {
                                    paymentRequestButton: {
                                        type: "check-out",
                                        theme: "light",
                                        height: "44px",
                                    },
                                },
                            }}
                        />
                    </div>
                    <div className="LoyaltyOfferCheckoutCardForm__or">
                        <span className="LoyaltyOfferCheckoutCardForm__orLine" />
                        <span className="LoyaltyOfferCheckoutCardForm__orText">Or check-out with card</span>
                        <span className="LoyaltyOfferCheckoutCardForm__orLine" />
                    </div>
                </>
            )}

            <label className="LoyaltyOfferCheckoutCardForm__label" htmlFor="nameOnCard">
                NAME ON CARD
            </label>
            <input
                id="nameOnCard"
                type="text"
                className="LoyaltyOfferCheckoutCardForm__input"
                placeholder="Example: Jane Doe"
                value={nameOnCard}
                onChange={(e) => setNameOnCard(e.target.value)}
                autoComplete="cc-name"
            />

            <label className="LoyaltyOfferCheckoutCardForm__label">CARD NUMBER</label>
            <div className="LoyaltyOfferCheckoutCardForm__element">
                <CardElement options={cardOptions}></CardElement>
            </div>

            {errorMsg && (
                <div className="LoyaltyOfferCheckoutCardForm__error">{errorMsg}</div>
            )}
            
            {/* <label className="LoyaltyOfferCheckoutCardForm__label">CONFIRM PAYMENT</label> */}
            <button
                className="LoyaltyOfferCheckoutCardForm__submit"
                type="submit"
                disabled={disabled}
                aria-busy={submitting}
            >
                Upgrade Now For 50% Off
            </button>
        </form>
    );
};

export default LoyaltyOfferCheckoutCardForm;
